import { useState } from 'react';
import { AirIdDisplay } from '@/components/air-id-display.js';
import { createTableRowSelectionCell } from '@/components/table/entity-table.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { CustomTableDialog } from '@/components/ui/custom-table-dialog.js';
import { CoachOverallGradeBadge } from '@/components/coaches/coach-overall-grade-badge.js';
import { pluralize } from '@/lib/string-utils.js';
import { airColumnHelper } from '@/lib/tables.js';
import { formatStudentClassTimeAbbr } from '@/routes/_layout/scheduling/schedules/-components/formatting.js';
import { PeopleBadgeList } from '@/routes/_layout/scheduling/student-groups/-components/people-badge-list.js';
import { LevelChangeWarning } from '@/routes/_layout/orgs/schools/-components/level-change-warning.js';
import { StudentGroupStatusBadge } from '@/routes/_layout/scheduling/student-groups/-components/student-group-status-badge.js';
import { formatInstant, formatPlainDate, FrequentTimeZones, safeInstantFromString } from '@airreading/common';
import { Temporal } from 'temporal-polyfill';
import {
  adjustMinimalClassTimeAcrossTZ,
  DayOfWeekLabelMap,
  getStudentGroupsSortableColumnSchema,
  OneTimeConflictSummary,
  SortedDaysOfWeek,
  StudentGroupWithScheduleAndLevel,
  StudentYear,
  StudentYearColorMap,
  studentYearOption,
} from '@airreading/common/data';
import { Link } from '@tanstack/react-router';
import { Pencil } from 'lucide-react';
import { ClassSessionsDrawer } from './class-sessions-drawer/index.js';

function ConflictsCell({ conflicts }: { conflicts: OneTimeConflictSummary[] | undefined }) {
  const [open, setOpen] = useState(false);
  const count = conflicts?.length ?? 0;
  const isClickable = count > 0;

  const anchor = (
    <div
      className={isClickable ? 'cursor-pointer hover:underline' : undefined}
      onClick={() => isClickable && setOpen(true)}
    >
      {count}
    </div>
  );

  return (
    <CustomTableDialog open={open} setOpen={setOpen} anchor={anchor}>
      <div className="flex flex-col divide-y border px-2">
        {conflicts?.map((conflict) => (
          <Link
            to={'/sessions/$sessionId'}
            params={{ sessionId: conflict.sessionId }}
            className="text-blue-600 py-2 text-sm"
          >
            {conflict.classDate}, {conflict.classTime} CT, {conflict.schoolName} - {conflict.districtName}
          </Link>
        ))}
      </div>
    </CustomTableDialog>
  );
}

export const columnHelper = airColumnHelper<StudentGroupWithScheduleAndLevel>(getStudentGroupsSortableColumnSchema);

export function getOneTimeClassTableColumns(schoolId: string, schoolTimeZone: string) {
  const columns = [
    createTableRowSelectionCell(columnHelper),

    columnHelper.accessor((row) => row, {
      id: 'student-group-id',
      header: '',
      cell: ({ getValue }) => {
        const studentGroup = getValue();
        return (
          <Link
            to="/orgs/schools/$schoolId/one-time-classes/$classId"
            params={{ schoolId, classId: studentGroup.id }}
            aria-label="Edit class"
          >
            <Pencil size={16} aria-hidden />
          </Link>
        );
      },
      size: 40,
    }),

    columnHelper.accessor((row) => row, {
      id: 'class-id-link',
      header: 'Class ID',
      cell: ({ getValue }) => {
        const studentGroup = getValue();
        return (
          <AirIdDisplay
            entityType="cls"
            rawId={studentGroup.id}
            wrapper={(displayId) => (
              <ClassSessionsDrawer
                trigger={
                  <Button variant="link" className="p-0 h-auto text-blue-600 hover:text-blue-800 no-underline">
                    {displayId}
                  </Button>
                }
                classId={studentGroup.id}
              />
            )}
          />
        );
      },
    }),

    columnHelper.accessor('coach', {
      id: 'coach',
      header: 'Teacher',
      cell: ({ getValue }) => {
        const coach = getValue();
        return coach ? <Badge variant={'outline'}>{coach.fullNameOrEmail}</Badge> : <div>No teacher assigned</div>;
      },
    }),

    columnHelper.accessor((row) => row, {
      id: 'overallGrade',
      header: 'Overall grade',
      cell: ({ getValue }) => {
        const { coach } = getValue();
        return <CoachOverallGradeBadge grade={coach?.coachProfile.overallGrade} />;
      },
    }),

    columnHelper.accessor('students', {
      id: 'students',
      header: 'Students',
      cell: ({ getValue }) => <PeopleBadgeList people={getValue()} wrap={true} />,
    }),

    columnHelper.accessor('grades', {
      id: 'grades',
      header: 'Grades',
      cell: ({ getValue }) => {
        const grades = getValue();
        if (!grades || grades.length === 0) return <div>-</div>;

        return (
          <div className="flex gap-1 flex-wrap">
            {grades.map((grade, index) => {
              const studentYear = grade as StudentYear;
              if (!studentYear) return null;
              const backgroundColor = StudentYearColorMap[studentYear];
              return (
                <span
                  key={index}
                  className="text-xs rounded-full px-2 py-1 min-w-9 text-center"
                  style={{ backgroundColor }}
                >
                  {studentYearOption(studentYear)}
                </span>
              );
            })}
          </div>
        );
      },
    }),

    columnHelper.accessor('status', {
      id: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        return <StudentGroupStatusBadge studentGroupLike={{ status: getValue() }} />;
      },
    }),

    columnHelper.accessor('classTime', {
      id: 'schedule',
      header: 'Schedule',
      cell: ({ getValue }) => {
        const { schedule } = getValue();
        return (
          <Badge variant="outline" className="text-nowrap">
            {schedule.name}
          </Badge>
        );
      },
    }),

    columnHelper.accessor((row) => row, {
      id: 'class-date',
      header: 'Class date',
      cell: ({ getValue }) => {
        const { classTime, overrideStartPlainDate } = getValue();
        const scheduledStartDate = classTime.schedule.startPlainDate;
        const displayStartDate = overrideStartPlainDate || scheduledStartDate;
        return <div>{formatPlainDate(displayStartDate)}</div>;
      },
    }),

    columnHelper.accessor((row) => row, {
      id: 'class-time',
      header: 'Class time (CT)',
      cell: ({ getValue }) => {
        const { classTime, overrideStartPlainDate } = getValue();
        const adjustedClassTime = adjustMinimalClassTimeAcrossTZ(
          classTime,
          schoolTimeZone,
          FrequentTimeZones.UsCentral,
        );
        const classDate = overrideStartPlainDate || classTime.schedule.startPlainDate;
        const day = SortedDaysOfWeek[Temporal.PlainDate.from(classDate).dayOfWeek - 1];
        const dayAbbr = DayOfWeekLabelMap[day].abbr;

        return (
          <Badge variant="outline" className="text-nowrap">
            {dayAbbr}, {formatStudentClassTimeAbbr(adjustedClassTime)}
          </Badge>
        );
      },
    }),

    columnHelper.accessor('learningPath', {
      id: 'learningPath',
      header: 'Learning Path',
      cell: ({ getValue }) => {
        return getValue().name;
      },
    }),

    columnHelper.accessor('startingLesson', {
      id: 'startingLesson',
      header: 'Starting lesson',
      cell: ({ getValue }) => {
        return <div>{getValue().name}</div>;
      },
    }),

    columnHelper.accessor('nextLesson', {
      id: 'nextLesson',
      header: 'Next lesson',
      cell: ({ getValue }) => {
        return <div>{getValue()?.name}</div>;
      },
    }),

    columnHelper.accessor('futureSessions', {
      id: 'futureSessions',
      header: 'Number of future sessions',
      cell: ({ getValue }) => {
        return <div>{getValue()}</div>;
      },
    }),

    columnHelper.accessor('students', {
      id: 'studentCount',
      header: 'Number of Students',
      cell: ({ getValue }) => getValue().length,
    }),

    columnHelper.accessor((row) => row, {
      id: 'createdAt',
      header: 'Created time (CT)',
      cell: ({ getValue }) => {
        const { createdAt } = getValue();
        const instant = safeInstantFromString(String(createdAt));
        const { formattedDateTime } = formatInstant(instant, FrequentTimeZones.UsCentral);
        return <div>{formattedDateTime}</div>;
      },
    }),

    columnHelper.accessor('notes', {
      id: 'notes',
      header: 'Notes',
      cell: ({ getValue }) => {
        const notes = getValue();
        return <div className="max-w-xs truncate">{notes || '-'}</div>;
      },
    }),
  ];

  return columns;
}

export function getSchoolClassTableColumns(schoolId: string, schoolTimeZone: string, showConflicts: boolean = false) {
  const columns = [
    createTableRowSelectionCell(columnHelper),

    columnHelper.accessor((row) => row, {
      id: 'student-group-id',
      header: '',
      cell: ({ getValue }) => {
        const studentGroup = getValue();
        return (
          <Link
            to="/orgs/schools/$schoolId/classes/$classId"
            params={{ schoolId, classId: studentGroup.id }}
            aria-label="Edit class"
          >
            <Pencil size={16} aria-hidden />
          </Link>
        );
      },
      size: 40,
    }),

    columnHelper.accessor((row) => row, {
      id: 'class-id-link',
      header: 'Class ID',
      cell: ({ getValue }) => {
        const studentGroup = getValue();
        return (
          <AirIdDisplay
            entityType="cls"
            rawId={studentGroup.id}
            wrapper={(displayId) => (
              <ClassSessionsDrawer
                trigger={
                  <Button variant="link" className="p-0 h-auto text-blue-600 hover:text-blue-800 no-underline">
                    {displayId}
                  </Button>
                }
                classId={studentGroup.id}
              />
            )}
          />
        );
      },
    }),

    columnHelper.accessor('coach', {
      id: 'coach',
      header: 'Teacher',
      cell: ({ getValue }) => {
        const coach = getValue();
        return coach ? <Badge variant={'outline'}>{coach.fullNameOrEmail}</Badge> : <div>No teacher assigned</div>;
      },
    }),

    ...(showConflicts
      ? [
          columnHelper.accessor('oneTimeConflicts', {
            id: 'oneTimeConflicts',
            header: 'Conflicts',
            cell: ({ getValue }) => {
              return <ConflictsCell conflicts={getValue()} />;
            },
          }),
        ]
      : []),

    columnHelper.accessor((row) => row, {
      id: 'overallGrade',
      header: 'Overall grade',
      cell: ({ getValue }) => {
        const { coach } = getValue();
        return <CoachOverallGradeBadge grade={coach?.coachProfile.overallGrade} />;
      },
    }),

    columnHelper.accessor('students', {
      id: 'students',
      header: 'Students',
      cell: ({ getValue }) => <PeopleBadgeList people={getValue()} wrap={true} renderExtra={LevelChangeWarning} />,
    }),

    columnHelper.accessor('grades', {
      id: 'grades',
      header: 'Grades',
      cell: ({ getValue }) => {
        const grades = getValue();
        if (!grades || grades.length === 0) return <div>-</div>;

        return (
          <div className="flex gap-1 flex-wrap">
            {grades.map((grade, index) => {
              const studentYear = grade as StudentYear;
              if (!studentYear) return null;
              const backgroundColor = StudentYearColorMap[studentYear];
              return (
                <span
                  key={index}
                  className="text-xs rounded-full px-2 py-1 min-w-9 text-center"
                  style={{ backgroundColor }}
                >
                  {studentYearOption(studentYear)}
                </span>
              );
            })}
          </div>
        );
      },
    }),

    columnHelper.accessor('status', {
      id: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        return <StudentGroupStatusBadge studentGroupLike={{ status: getValue() }} />;
      },
    }),

    columnHelper.accessor('classTime', {
      id: 'schedule',
      header: 'Schedule',
      cell: ({ getValue }) => {
        const { schedule } = getValue();
        return (
          <Badge variant="outline" className="text-nowrap">
            {schedule.name}
          </Badge>
        );
      },
    }),

    columnHelper.accessor((row) => row, {
      id: 'start-date',
      header: 'Start date',
      cell: ({ getValue }) => {
        const { classTime, overrideStartPlainDate } = getValue();
        const scheduledStartDate = classTime.schedule.startPlainDate;
        const displayStartDate = overrideStartPlainDate || scheduledStartDate;
        return <div>{formatPlainDate(displayStartDate)}</div>;
      },
    }),

    columnHelper.accessor((row) => row, {
      id: 'end-date',
      header: 'End date',
      cell: ({ getValue }) => {
        const { classTime, overrideEndPlainDate } = getValue();
        const scheduledEndDate = classTime.schedule.endPlainDate;
        const displayEndDate = overrideEndPlainDate || scheduledEndDate;
        return <div>{formatPlainDate(displayEndDate)}</div>;
      },
    }),

    columnHelper.accessor('classTime', {
      id: 'class-time',
      header: 'Class time (CT)',
      cell: ({ getValue }) => {
        const classTime = getValue();
        return (
          <Badge variant="outline" className="text-nowrap">
            {formatStudentClassTimeAbbr(
              adjustMinimalClassTimeAcrossTZ(classTime, schoolTimeZone, FrequentTimeZones.UsCentral),
            )}
          </Badge>
        );
      },
    }),

    columnHelper.accessor('learningPath', {
      id: 'learningPath',
      header: 'Learning Path',
      cell: ({ getValue }) => {
        return getValue().name;
      },
    }),

    columnHelper.accessor('startingLesson', {
      id: 'startingLesson',
      header: 'Starting lesson',
      cell: ({ getValue }) => {
        return <div>{getValue().name}</div>;
      },
    }),

    columnHelper.accessor('nextLesson', {
      id: 'nextLesson',
      header: 'Next lesson',
      cell: ({ getValue }) => {
        return <div>{getValue()?.name}</div>;
      },
    }),

    columnHelper.accessor('futureSessions', {
      id: 'futureSessions',
      header: 'Number of future sessions',
      cell: ({ getValue }) => {
        return <div>{getValue()}</div>;
      },
    }),

    columnHelper.accessor('students', {
      id: 'studentCount',
      header: 'Number of Students',
      cell: ({ getValue }) => getValue().length,
    }),

    columnHelper.accessor((row) => row, {
      id: 'createdAt',
      header: 'Created time (CT)',
      cell: ({ getValue }) => {
        const { createdAt } = getValue();
        const instant = safeInstantFromString(String(createdAt));
        const { formattedDateTime } = formatInstant(instant, FrequentTimeZones.UsCentral);
        return <div>{formattedDateTime}</div>;
      },
    }),

    columnHelper.accessor((row) => row, {
      id: 'lastManualUpdatedAt',
      header: 'Last manual updated time (CT)',
      cell: ({ getValue }) => {
        const { lastManualUpdatedAt } = getValue();
        if (!lastManualUpdatedAt) {
          return <div>-</div>;
        }
        const instant = safeInstantFromString(String(lastManualUpdatedAt));
        const { formattedDateTime } = formatInstant(instant, FrequentTimeZones.UsCentral);
        return <div>{formattedDateTime}</div>;
      },
    }),

    columnHelper.accessor('coachAssignedAtPlainDate', {
      id: 'coachAssignedAtPlainDate',
      header: 'Teacher assigned time',
      cell: ({ getValue }) => {
        const coachAssignedAt = getValue();
        if (!coachAssignedAt) {
          return <div>-</div>;
        }
        return <div>{formatPlainDate(coachAssignedAt)}</div>;
      },
    }),

    columnHelper.accessor('maxPlannedAbsences', {
      id: 'maxPlannedAbsences',
      header: 'Sub request limit',
      cell: ({ getValue }) => {
        const maxPlannedAbsences = getValue();
        if (maxPlannedAbsences === 0) {
          return <div>-</div>;
        }
        const displayValue = `${maxPlannedAbsences} ${pluralize(maxPlannedAbsences, 'class', 'classes')}`;
        return <div>{displayValue}</div>;
      },
    }),

    columnHelper.accessor('notes', {
      id: 'notes',
      header: 'Notes',
      cell: ({ getValue }) => {
        const notes = getValue();
        return <div className="max-w-xs truncate">{notes || '-'}</div>;
      },
    }),
  ];

  return columns;
}
